import api from '@/helpers/api'
import helpers from '@/helpers'
import i18n from '@/plugins/i18n'
import store from '@/store'
import router from '../router/index'

const REQUIRED_ROLES = ['merchant_admin', 'merchant_user', 'parkman_admin']

export default {
  /**
   * Try to refresh user token.
   *
   * @returns {Promise<void>}
   */
  async refreshToken() {
    const apiSettings = {
      method: 'put',
      service: 'users',
      url: 'v1/tokens/refresh',
      data: {
        refresh_token: helpers.fetchStorage('pb-refresh-token'),
        platform: 'web'
      },
      _retry: true
    }

    try {
      const response = await api.promise(apiSettings)
      helpers.saveStorage('pb-token', response.tokens.access_token)
      helpers.saveStorage('pb-refresh-token', response.tokens.refresh_token)
    } catch (error) {
      await this.$store.dispatch(
        'setSystemMessage',
        this.$t('g_unable_refresh_token')
      )
    }
  },

  /**
   * Sign in merchant
   *
   * @param data
   * @returns {Promise<*>}
   */
  async signInMerchant(data) {
    const apiSettings = {
      method: 'post',
      service: 'users',
      url: 'v1/merchant/login',
      data: data
    }

    try {
      const response = await api.promise(apiSettings)

      if (
        !response.roles.some((role) =>
          REQUIRED_ROLES.includes(role.role_name || role.name)
        )
      ) {
        await store.dispatch('setSystemMessage', {
          message: i18n.t('g_invalid_authority'),
          type: 'error'
        })

        return false
      }

      helpers.saveStorage('pb-token', response.token.access_token)
      helpers.saveStorage('pb-refresh-token', response.token.refresh_token)
      helpers.saveStorage('user-data', response.user)

      await Promise.all([
        store.dispatch('setRoleOptions', response.roles),
        store.dispatch('setMerchantAccounts', response.merchant_accounts),
        store.dispatch('setUserData', response.user)
      ])

      return response
    } catch (errors) {
      const [firstError] = errors
      let errorMessage = ''

      if (firstError?.type === 'code_not_found') {
        errorMessage = i18n.t('g_invalid_code')
      } else if (firstError?.type === 'user_not_found') {
        errorMessage = i18n.t('user_account_not_found')
      } else {
        errorMessage = firstError.message
      }

      await store.dispatch('setSystemMessage', {
        message: errorMessage,
        type: 'error'
      })

      return false
    }
  },
  logout() {
    localStorage.clear()
    window.location.href = '/'
  },
  /**
   * Verify that user is authenticated and token matches
   * @returns {Boolean}
   */
  loggedIn() {
    const token = helpers.fetchStorage('pb-token')

    if (!token) {
      return false
    }

    const tokenInfo = helpers.getTokenInfo()
    return tokenInfo && tokenInfo.uid
  },
  accessTokenExpiredButRefreshTokenValid() {
    const token = helpers.getTokenInfo()
    const refreshToken = helpers.getRefreshTokenInfo()

    if (!token || !refreshToken) {
      return false
    }
    const now = Math.floor(Date.now() / 1000)

    if (token && token.exp < now && refreshToken && refreshToken.exp > now) {
      return true
    }

    return false
  },
  /**
   * Check from storage that user is group admin
   * This is different from the method in userHelpers - which check the role based on user object
   * This is faster but not as reliable, used when UI first loaded and backend calling is not complete
   * @todo - come back to check this
   */
  isGroupAdmin() {
    return Boolean(helpers.fetchStorage('group-id'))
  },
  /**
   * Check if the current role is allowed to perform the specified action
   * @param {String} action
   * @return {Boolean} true if the action is allowed, false otherwise
   */
  checkPermission(action) {
    const DISABLED_ACTIONS = {
      GROUP_ADMIN: [
        'view-all-groups',
        'add-group',
        'delete-group',
        'edit-company',
        'set-company-admin-role'
      ]
    }

    // check if user is group admin. If not, allow all actions
    if (!this.isGroupAdmin()) {
      return true
    }
    // check if the action is within the restriction list
    return DISABLED_ACTIONS.GROUP_ADMIN.indexOf(action) === -1
  },

  hasRole(role) {
    return !!store.getters.roleOptions?.find(
      (item) => (item.role_name || item.name) === role
    )
  },

  isMerchantAdmin() {
    const hasRole = this.hasRole('merchant_admin')

    const merchantId = router?.currentRoute?.params?.merchantId

    if (merchantId) {
      const hasAccountRole = !!store.getters.accountRoleOptions.find(
        (item) =>
          item.merchant_id === +merchantId && item.role === 'merchant_admin'
      )

      return hasRole && hasAccountRole
    }

    return hasRole
  },

  isMerchantUser() {
    const hasRole = this.hasRole('merchant_user')

    const merchantId = router?.currentRoute?.params?.merchantId

    if (merchantId) {
      const hasAccountRole = !!store.getters.accountRoleOptions.find(
        (item) =>
          item.merchant_id === +merchantId && item.role === 'merchant_user'
      )

      return hasRole && hasAccountRole
    }

    return hasRole
  },

  isParkmanAdmin() {
    // For testing reason should be possibility to switch role
    if (helpers.fetchStorage('switch-to-merchant-admin')) {
      return false
    }

    return this.hasRole('parkman_admin')
  },

  switchAdminRole() {
    if (this.isParkmanAdmin()) {
      helpers.saveStorage('switch-to-merchant-admin', '1')
    } else {
      helpers.clearStorage('switch-to-merchant-admin')
    }
  },

  getUserCountry() {
    return this.getUserData()?.country ?? '1'
  },

  getUserData() {
    return helpers.fetchStorage('user-data')
  }
}
